import { useEffect, useEffectEvent, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ImagePickerAsset } from 'expo-image-picker';
import { AxiosError } from 'axios';
import Toast from 'react-native-toast-message';
import { useAnalyzeListingImage } from 'app/network/listing';
import type { useForm } from 'app/hooks/form';
import { ListingType, type CreateListingDTO } from 'app/types/listing';
import type { SpecificRouteProp } from 'app/types/navigation';
import type { NewScreenParams } from './index';

type OfferCreationStep = 'image' | 'generate' | 'details' | 'image-and-details';

function arePicturesEquivalent(left?: ImagePickerAsset | null, right?: ImagePickerAsset | null) {
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.assetId && right.assetId) return left.assetId === right.assetId;
  return left.uri === right.uri;
}

function getInitialOfferCreationStep(defaultValues?: NewScreenParams['defaultValues']): OfferCreationStep {
  if (defaultValues?.type && defaultValues.type !== ListingType.offer) {
    return 'details';
  }

  const hasPrefilledDetails = Boolean(
    defaultValues?.description ||
    defaultValues?.item?.name ||
    defaultValues?.categoryPrimaryId ||
    defaultValues?.estimatedPrice ||
    defaultValues?.price ||
    defaultValues?.inventory ||
    defaultValues?.quantity ||
    defaultValues?.loanFee ||
    defaultValues?.loanFeeDescription ||
    defaultValues?.loanFeePaymentMethod ||
    defaultValues?.loanFeeDocument ||
    defaultValues?.serviceCategoryId ||
    defaultValues?.pictureUrl ||
    defaultValues?.groupIds?.length ||
    defaultValues?.addressIds?.length ||
    defaultValues?.transactionMode,
  );
  const hasPicture = Boolean(defaultValues?.pictureUrl);
  return hasPrefilledDetails && hasPicture ? 'image-and-details' : hasPrefilledDetails ? 'details' : 'image';
}

export function useOfferImageAnalysis({
  form,
  isOffer,
  route,
}: {
  form: ReturnType<typeof useForm<CreateListingDTO>>;
  isOffer: boolean;
  route: SpecificRouteProp<'new'>;
}) {
  const { t } = useTranslation('new');
  const [offerCreationStep, setOfferCreationStep] = useState<OfferCreationStep>(() =>
    getInitialOfferCreationStep(route.params?.defaultValues),
  );
  const description = useWatch({ control: form.control, name: 'description' });
  const title = useWatch({ control: form.control, name: 'item.name' });
  const pictures = useWatch({ control: form.control, name: 'pictures' });
  const hasPictures = (pictures?.length ?? 0) > 0;
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAIBanner, setShowAIBanner] = useState(false);

  const firstPicture = pictures?.[0];
  const { mutate: analyzeImage } = useAnalyzeListingImage();

  const synchronizeOfferCreationStep = useEffectEvent(() => {
    if (!isOffer) {
      setOfferCreationStep('details');
      return;
    }

    if (offerCreationStep === 'image' && hasPictures) {
      setOfferCreationStep('generate');
      return;
    }

    if (
      (offerCreationStep === 'generate' ||
        offerCreationStep === 'details' ||
        offerCreationStep === 'image-and-details') &&
      !hasPictures &&
      !isAnalyzing
    ) {
      if (offerCreationStep === 'generate') {
        setOfferCreationStep('image');
      } else if (offerCreationStep === 'image-and-details' || description || title) {
        setOfferCreationStep('image-and-details');
      } else {
        setOfferCreationStep('image');
      }
    }
  });
  useEffect(() => {
    const timeout = setTimeout(synchronizeOfferCreationStep, 0);
    return () => clearTimeout(timeout);
  }, [description, hasPictures, isAnalyzing, isOffer, offerCreationStep, title]);

  const onPicturesChange = (images: ImagePickerAsset[]) => {
    if (!isOffer) return;
    if (images.length === 0) {
      setShowAIBanner(false);
      if (offerCreationStep === 'generate' && !isAnalyzing) setOfferCreationStep('image');
    } else if (offerCreationStep === 'image') {
      // Advance in the picker event so the prompt and details appear together.
      setOfferCreationStep('generate');
      setShowAIBanner(true);
    } else if (offerCreationStep === 'image-and-details') {
      setShowAIBanner(true);
    }
  };

  const triggerImageAnalysis = () => {
    if (isAnalyzing || !firstPicture) return;

    const pictureAtCall = firstPicture;
    setOfferCreationStep('generate');
    setIsAnalyzing(true);

    analyzeImage(pictureAtCall, {
      onSuccess: (result: { title: string; description: string }) => {
        setIsAnalyzing(false);

        const currentFirstPicture = form.getValues('pictures')?.[0];
        if (!arePicturesEquivalent(currentFirstPicture, pictureAtCall)) return;

        form.setValue('item.name', result.title, { shouldDirty: true, shouldValidate: true });
        form.setValue('description', result.description, { shouldDirty: true, shouldValidate: true });
        setShowAIBanner(false);
        setOfferCreationStep('details');
      },
      onError: (error: Error) => {
        setIsAnalyzing(false);

        const currentFirstPicture = form.getValues('pictures')?.[0];
        if (!arePicturesEquivalent(currentFirstPicture, pictureAtCall)) return;

        const isRateLimit = error instanceof AxiosError && error.response?.status === 429;
        Toast.show({
          type: 'error',
          text1: isRateLimit
            ? t('itemInformationSection.aiGenerate.rateLimitError')
            : t('itemInformationSection.aiGenerate.error'),
        });
        setOfferCreationStep('details');
      },
    });
  };

  const skipOfferPictureStep = () => setOfferCreationStep('image-and-details');
  const skipOfferGenerationStep = () => {
    setOfferCreationStep('details');
    setShowAIBanner(false);
  };

  return {
    description,
    hasPictures,
    isAnalyzing,
    showAIBanner,
    onPicturesChange,
    offerCreationStep,
    triggerImageAnalysis,
    skipOfferPictureStep,
    skipOfferGenerationStep,
  };
}
